/**
 * TextValueLabel - Display component for text values with @mention support.
 *
 * Parses mention tags and renders them as styled badges. Supports all formats:
 * - <Entity type="X" entity-id="Y">Name</Entity>
 * - <NamedEntity type="X" id="Y">Name</NamedEntity> (legacy)
 * - <mention type="X" entity-id="Y">Name</mention> (legacy)
 * - <@entity_id|Display Name> (legacy Slack-style)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { ParsedMention } from '@/lib/search';
import { parseMentions, renderTextWithMentions } from '@/lib/search';

export interface TextValueLabelProps {
  value: string | null | undefined;
  /** Organization ID for clickable mention navigation */
  orgId?: number;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
  /** Truncate text to this many characters with an inline expand/collapse toggle */
  maxLength?: number;
  /**
   * When true, truncation still occurs (if `maxLength` is set) but the inline
   * "more/less" button is NOT rendered. Use this when a parent component owns
   * the expand/collapse state (e.g. a coordinated diff view that toggles both
   * old and new values together).
   */
  hideExpandButton?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

// Legacy Slack-style mentions: <@entity_id|Display Name>
const SLACK_MENTION_PATTERN = /(<@[^|>]+\|[^>]+>)/g;
const SLACK_MENTION_EXTRACT = /^<@([^|>]+)\|([^>]+)>$/;

/**
 * Find a safe truncation point that does not cut through an entity mention tag
 * or a Slack-style mention. If `maxLength` falls inside a tag boundary, snap
 * back to the start of that tag; otherwise return `maxLength` unchanged.
 */
function findSafeTruncationPoint(value: string, maxLength: number): number {
  const entityRanges = parseMentions(value).map(m => ({
    start: m.startIndex,
    end: m.endIndex,
  }));

  SLACK_MENTION_PATTERN.lastIndex = 0;
  const slackRanges: { start: number; end: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = SLACK_MENTION_PATTERN.exec(value)) !== null) {
    slackRanges.push({
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  const allRanges = [...entityRanges, ...slackRanges];
  for (const { start, end } of allRanges) {
    if (start < maxLength && maxLength < end) {
      return start;
    }
  }
  return maxLength;
}

/**
 * Render a single parsed entity mention when `orgId` is absent: a static
 * `EntityBadge` using the display name embedded in the mention tag, or — for
 * a self-closing tag with no inner name and no orgId to resolve it — the
 * defined "Unavailable" fallback (never raw markup, never a crash).
 */
function renderStaticMentionBadge(mention: ParsedMention): React.ReactNode {
  if (!mention.name) {
    return (
      <EntityBadge
        key={`mention-${mention.startIndex}`}
        state={{ kind: 'failed' }}
        variant='inline'
      />
    );
  }
  return (
    <EntityBadge
      key={`mention-${mention.startIndex}`}
      state={{
        kind: 'data',
        data: {
          id: mention.entity_id,
          entityType: mention.type,
          displayName: mention.name,
        },
      }}
      variant='inline'
    />
  );
}

/**
 * Render a text string with mention parsing (Entity, NamedEntity, Slack-style).
 *
 * When `orgId` is provided, entity mentions render as `EntityBadgeById` (live
 * store subscription, clickable slideout). When `orgId` is absent, they render
 * as a static `EntityBadge` using the display name embedded in the mention tag.
 */
function renderWithMentions(
  text: string,
  orgId: number | undefined
): React.ReactNode[] {
  const withEntityMentions = renderTextWithMentions(text, mention =>
    orgId !== undefined ? (
      <EntityBadgeById
        key={`mention-${mention.startIndex}`}
        entityId={mention.entity_id}
        orgId={orgId}
        variant='inline'
      />
    ) : (
      renderStaticMentionBadge(mention)
    )
  );

  return withEntityMentions.flatMap((node, nodeIndex) => {
    if (typeof node !== 'string') {
      return node;
    }

    const parts = node.split(SLACK_MENTION_PATTERN);
    return parts.map((part, partIndex) => {
      const mentionMatch = part.match(SLACK_MENTION_EXTRACT);
      if (mentionMatch) {
        const [, entityId, displayName] = mentionMatch;
        if (orgId !== undefined) {
          return (
            <EntityBadgeById
              // eslint-disable-next-line react/no-array-index-key -- Text fragments from mention parsing have no stable identity
              key={`slack-${nodeIndex}-${partIndex}`}
              entityId={entityId}
              orgId={orgId}
              variant='inline'
            />
          );
        }
        return (
          <EntityBadge
            // eslint-disable-next-line react/no-array-index-key -- Text fragments from mention parsing have no stable identity
            key={`slack-${nodeIndex}-${partIndex}`}
            state={{
              kind: 'data',
              data: { id: entityId, entityType: 'ahoy_user', displayName },
            }}
            variant='inline'
          />
        );
      }
      return part ? (
        // eslint-disable-next-line react/no-array-index-key -- Text fragments from mention parsing have no stable identity
        <span key={`text-${nodeIndex}-${partIndex}`}>{part}</span>
      ) : null;
    });
  });
}

/**
 * Display component for text values with @mention support.
 *
 * @example
 * <TextValueLabel value='Hello <Entity type="teammate" entity-id="abc">John</Entity>!' />
 * // Renders: "Hello @John!" with @John in a styled badge
 */
export const TextValueLabel: React.FC<TextValueLabelProps> = ({
  value,
  orgId,
  fontSize = 'sm',
  className = '',
  truncate,
  maxLength,
  hideExpandButton,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  const [isExpanded, setIsExpanded] = useState(false);

  // Reset expanded state when value or maxLength changes
  useEffect(() => {
    setIsExpanded(false);
  }, [value, maxLength]);

  const handleToggleExpanded = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  const exceedsMaxLength =
    maxLength != null && value != null && value.length > maxLength;

  // Compute the text to display based on maxLength and expanded state
  const displayValue =
    exceedsMaxLength && !isExpanded && value != null
      ? value.slice(0, findSafeTruncationPoint(value, maxLength)) + '…'
      : value;

  const renderedContent = useMemo(() => {
    if (
      displayValue === null ||
      displayValue === undefined ||
      displayValue === ''
    ) {
      return null;
    }
    return renderWithMentions(displayValue, orgId);
  }, [displayValue, orgId]);

  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  // When truncating, override whitespace-pre-wrap with whitespace-nowrap
  const whitespaceClass = truncate ? '' : 'whitespace-pre-wrap';

  // `min-w-0` lets this block-level span shrink below the intrinsic content
  // width when rendered as a flex item (e.g. inside the multi-value wrapper
  // in PropertyValueLabel, or in OverviewSection's property rows). Without
  // it, the span sizes to its content (e.g. an email at ~200px) and never
  // gives `text-overflow: ellipsis` a smaller-than-content width to clip
  // against — so the ellipsis silently does nothing in narrow contexts.
  const truncateShrinkClass = truncate ? 'min-w-0' : '';

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${truncateShrinkClass} ${whitespaceClass} break-words ${className}`.trim()}
      title={truncate ? value : undefined}
    >
      {renderedContent}
      {exceedsMaxLength && !hideExpandButton && (
        <button
          type='button'
          onClick={handleToggleExpanded}
          className='ml-1 text-utility-brand-600 hover:text-utility-brand-700 dark:text-utility-brand-400 dark:hover:text-utility-brand-300 font-medium cursor-pointer'
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Show less' : 'Show more'}
        >
          {isExpanded ? 'less' : 'more'}
        </button>
      )}
    </span>
  );
};
